"use client";

import { useEffect } from "react";

const SCRIPT_ID = "kettura-vchat";
const CHECK_INTERVAL_MS = 10_000;
const CHECK_TIMEOUT_MS = 5_000;

export default function Chatbot({ preview = false }: { preview?: boolean }) {
  useEffect(() => {
    let disposed = false;
    let script: HTMLScriptElement | null = null;
    let request: AbortController | null = null;
    let reloading = false;

    function stopChatbot() {
      document.querySelectorAll("[data-vchat-widget]").forEach((host) => host.remove());
      if (!script || reloading) return;
      reloading = true;
      script.remove();
      // Removing a script/DOM node does not stop its listeners or an active stream.
      // VChat has no teardown API. A fresh document fully stops its execution and
      // must pass the uncached status check again before loading any VChat code.
      window.location.reload();
    }

    async function checkStatus() {
      if (disposed || request || reloading) return;
      const controller = new AbortController();
      request = controller;
      const timeout = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
      try {
        const response = await fetch("/api/chatbot/status", {
          cache: "no-store",
          signal: controller.signal,
        });
        const status = response.ok ? await response.json() : null;
        if (disposed) return;

        if (status?.enabled !== true) {
          stopChatbot();
        } else if (!script) {
          script = document.createElement("script");
          script.id = SCRIPT_ID;
          script.async = true;
          script.src = preview
            ? "/api/chatbot/preview/embed.js"
            : "https://vchat-debian-13-iwtfuwxc.lpd2ffa4aa.linqprotocol.com/embed.js";
          if (preview) script.dataset.api = `${window.location.origin}/api/chatbot/preview`;
          script.dataset.tenant = "pk_live_GjjayIrVcbe5X_WUaPJ7uuus7iATajRz";
          document.body.appendChild(script);
        }
      } catch {
        if (!disposed) stopChatbot();
      } finally {
        window.clearTimeout(timeout);
        request = null;
      }
    }

    function checkWhenVisible() {
      if (document.visibilityState === "visible") void checkStatus();
    }

    void checkStatus();
    const interval = window.setInterval(checkStatus, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", checkWhenVisible);
    window.addEventListener("pageshow", checkWhenVisible);
    window.addEventListener("online", checkWhenVisible);

    return () => {
      disposed = true;
      request?.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", checkWhenVisible);
      window.removeEventListener("pageshow", checkWhenVisible);
      window.removeEventListener("online", checkWhenVisible);
      // Public layouts persist between public routes. Leaving for the admin
      // console must also stop a previously loaded widget completely.
      stopChatbot();
    };
  }, [preview]);

  return null;
}
